import { Button, Loader, Table, Tooltip } from '@mantine/core';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import TimeAgo from '~/components/time/TimeAgo';
import { useProjectId } from '~/hooks/context/useProjectId';
import type { TestDraftListItem } from '~/server/api/routers/testDraft';
import { api } from '~/trpc-extra/api';

const CreatedBy = ({ email }: { email: string }) => {
  const emailPrefix = email?.split('@').at(0);
  return (
    emailPrefix && (
      <Tooltip label={email}>
        <div className="w-fit rounded-xl bg-indigo-300/20 p-1 px-3 text-xs font-medium text-indigo-500 first-letter:capitalize dark:text-indigo-300">
          {email.split('@')[0]}
        </div>
      </Tooltip>
    )
  );
};

export default function TestDraftTable({ testId }: { testId: string }) {
  const router = useRouter();
  const projectId = useProjectId();
  const { data: testDrafts, isLoading: testDraftsLoading } = api.testDraft.listByTestId.useQuery({
    testId: testId || '',
  });

  const createDraftForTest = api.testDraft.forkTrunk.useMutation({
    onSuccess: async (testDraft) => {
      await router.push(`/project/${projectId}/testDraft/${testDraft.id}?fromTest=true`);
    },
  });

  const userIds = useMemo(
    () => [...new Set(testDrafts?.map((draft) => draft?.createdByUserId).filter((userId) => userId !== null))],
    [testDrafts],
  );
  const { data: users } = api.team.getUsersInfo.useQuery({ userIds }, { enabled: userIds.length > 0 });
  const handleRowClick = useCallback(
    async (e: React.MouseEvent, draft: TestDraftListItem) => {
      e.stopPropagation();
      await router.push(`/project/${projectId}/testDraft/${draft.id}`).catch(() => undefined);
    },
    [projectId, router],
  );

  return (
    testDraftsLoading ?
      <div className="flex h-full w-full items-center justify-center">
        <Loader />
      </div>
    : testDrafts && testDrafts.length > 0 ?
      <div>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          These are unpublished changes for this test. Click on a draft to edit it.
        </div>
        <div className="mt-8 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Created By</Table.Th>
                <Table.Th>Last Edited</Table.Th>
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              {(testDrafts || [])?.map((draft: TestDraftListItem) => (
                <Table.Tr
                  key={draft.id}
                  onClick={(e) => handleRowClick(e, draft)}
                >
                  <Table.Td className="mr-2 py-3 font-medium first-letter:capitalize hover:cursor-pointer hover:text-blue-800 dark:hover:text-blue-200">
                    <Link href={`/project/${projectId}/testDraft/${draft.id}`}>{draft.test.name}</Link>
                  </Table.Td>
                  <Table.Td className="mr-2 py-3 font-medium first-letter:capitalize hover:cursor-pointer hover:text-blue-800 dark:hover:text-blue-200">
                    {users && draft?.createdByUserId ?
                      <CreatedBy email={users[draft.createdByUserId]?.email} />
                    : null}
                  </Table.Td>

                  <Table.Td>
                    <TimeAgo
                      time={draft.updatedAt}
                      textStyle="text-stone-700 dark:text-stone-400"
                    />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>
      </div>
    : <div className="px-2 pt-4 font-medium text-stone-800 dark:text-stone-200">
        <div className="mb-4 text-gray-600 dark:text-gray-400">
          No test drafts yet. Click &apos;Edit Test&apos; to create one.
        </div>
        <Button
          onClick={async () => {
            await createDraftForTest.mutateAsync({ testId });
          }}
        >
          Edit Test
        </Button>
      </div>
  );
}
